import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Questionnaire } from "../Questionnaire";
import { Modal } from "../Modal";
import { log, warn } from "../../utils/logger";
import "./Anketa.css";

const REVENUE_YEAR = 2026;

const MONTH_NAMES_RU = [
    "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
    "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

function daysInMonth(month: number, year: number): number {
    return new Date(year, month, 0).getDate();
}

function toIsoFromParts(month: number, day: number): string {
    return `${REVENUE_YEAR}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Дата строго в диапазоне 1..2026-12-31 с учётом длины месяца */
function isValidRevenueDate2026(iso: string): boolean {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return false;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (y !== REVENUE_YEAR) return false;
    if (mo < 1 || mo > 12) return false;
    const maxD = daysInMonth(mo, REVENUE_YEAR);
    if (d < 1 || d > maxD) return false;
    const date = new Date(REVENUE_YEAR, mo - 1, d);
    return date.getFullYear() === REVENUE_YEAR && date.getMonth() === mo - 1 && date.getDate() === d;
}

/** Последняя допустимая календарная дата в 2026 г. не позже «сегодня» (локальное время). До 01.01.2026 — null. */
function getMaxSelectableDateIn2026(): Date | null {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end2026 = new Date(2026, 11, 31);
    end2026.setHours(0, 0, 0, 0);
    if (today > end2026) return end2026;
    const start2026 = new Date(2026, 0, 1);
    start2026.setHours(0, 0, 0, 0);
    if (today < start2026) return null;
    return today;
}

function isRevenueDateNotInFuture(iso: string): boolean {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return false;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const selected = new Date(y, mo - 1, d);
    selected.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selected <= today;
}

function isCompleteRevenueDate(iso: string): boolean {
    return isValidRevenueDate2026(iso) && isRevenueDateNotInFuture(iso);
}

/** Максимальный день для месяца `month` при ограничении сверху датой `maxDate` (в пределах 2026). */
function getMaxDayForMonthWithCap(maxDate: Date, month: number): number {
    const maxMonth = maxDate.getMonth() + 1;
    const capDay = maxDate.getDate();
    if (month > maxMonth) return 0;
    if (month < maxMonth) return daysInMonth(month, REVENUE_YEAR);
    return Math.min(capDay, daysInMonth(month, REVENUE_YEAR));
}

export const Register = () => {
    const navigate = useNavigate();
    const { login } = useAuth();

    const [step, setStep] = useState(1);
    const [endpointAddress, setEndpointAddress] = useState<string>("");

    // Загрузка конфигурации
    useEffect(() => {
        fetch('/config.json')
            .then(res => res.json())
            .then(config => {
                log('Register', 'Загружен config', { endpoint: config['endpoint-address'] });
                if (config['endpoint-address']) {
                    setEndpointAddress(config['endpoint-address']);
                } else {
                    warn('Register', 'endpoint-address не найден в config.json');
                }
            })
            .catch(err => log('Register', 'Ошибка загрузки config', err));
    }, []);

    // Вопросы для первого этапа регистрации
    const initialQuestions = [
        { id: 1, text: "Превысила ли выручка 20 млн рублей за прошлый год?" },
        { id: 2, text: "Была ли выручка с начала года до подключения наших услуг?" },
    ];

    const [answers, setAnswers] = useState({
        revenueExceeded20M: null as boolean | null,
        hadRevenueBeforeService: null as boolean | null,
        revenueStartDate2026: "",
    });
    const [tariffType, setTariffType] = useState<string | null>(null);

    const [revenueMonth, setRevenueMonth] = useState(1);
    const [revenueDay, setRevenueDay] = useState(1);

    const [userData, setUserData] = useState({
        fullName: "",
        inn: "",
        phone: "",
        email: "",
        password: "",
        registrationAddress: "",
        passportSeries: "",
        passportNumber: "",
        passportIssuedBy: "",
        passportIssueDate: "",
        snils: "",
        bankName: "",
        bankAccount: "",
        bankBik: "",
    });

    const [agreements, setAgreements] = useState({
        offerAgreement: false,
        personalDataAgreement: false,
    });

    const [displayProgress, setDisplayProgress] = useState(0);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [modal, setModal] = useState<{ open: boolean; type: "success" | "error"; text: string }>({ open: false, type: "success", text: "" });
    const [warningModal, setWarningModal] = useState<{ open: boolean; onContinue: () => void }>({ open: false, onContinue: () => {} });
    const [documentModal, setDocumentModal] = useState<{ open: boolean; type: "offer" | "privacy" }>({ open: false, type: "offer" });
    const [showPassword, setShowPassword] = useState(false);

    const maxRevenueDate2026 = step === 3 ? getMaxSelectableDateIn2026() : null;

    // Анимация прогресса для шагов
    useEffect(() => {
        const targetProgress = step === 1 ? 33 : step === 2 ? 66 : step === 3 ? 100 : 100;

        const duration = 500;
        const startTime = Date.now();
        let startProgress = displayProgress;

        const animateProgress = () => {
            const currentTime = Date.now();
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            const newProgress = startProgress + (targetProgress - startProgress) * easeProgress;

            setDisplayProgress(newProgress);

            if (progress < 1) {
                requestAnimationFrame(animateProgress);
            }
        };

        requestAnimationFrame(animateProgress);
    }, [step]);

    // Шаг 3: синхронизация полей с ISO-строкой (в т.ч. при повторном входе на шаг)
    useEffect(() => {
        if (step !== 3) return;
        const max = getMaxSelectableDateIn2026();
        if (!max) {
            setAnswers((prev) =>
                prev.revenueStartDate2026 ? { ...prev, revenueStartDate2026: "" } : prev
            );
            setRevenueMonth(1);
            setRevenueDay(1);
            return;
        }
        const iso = answers.revenueStartDate2026;
        if (iso && isCompleteRevenueDate(iso)) {
            const [, mo, d] = iso.split("-").map(Number);
            setRevenueMonth(mo);
            setRevenueDay(d);
        } else {
            const m = max.getMonth() + 1;
            const d = max.getDate();
            setRevenueMonth(m);
            setRevenueDay(d);
            setAnswers((prev) => ({ ...prev, revenueStartDate2026: toIsoFromParts(m, d) }));
        }
    }, [step, answers.revenueStartDate2026]);

    // Обработка завершения анкеты (шаг 2)
    const handleQuestionnaireComplete = (questionnaireAnswers: Record<number, string>) => {
        // Сохраняем ответы и определяем тариф по первому вопросу: "Да" = "2" (УСН с НДС), "Нет" = "1" (УСН без НДС)
        const newAnswers = { ...answers };
        let firstQuestionAnswer: string | null = null;
        let tariffFromAnswer = "1";
        Object.entries(questionnaireAnswers).forEach(([key, value]) => {
            if (parseInt(key) === 1) {
                const normalized = value.trim().toUpperCase();
                newAnswers.revenueExceeded20M = normalized === 'ДА';
                firstQuestionAnswer = normalized;
                tariffFromAnswer = normalized === 'ДА' ? "2" : "1";
                setTariffType(tariffFromAnswer);
            } else if (parseInt(key) === 2) {
                newAnswers.hadRevenueBeforeService = value.trim().toUpperCase() === 'ДА';
            }
        });
        setAnswers(newAnswers);

        // Если на первый вопрос ответили "НЕТ" (тариф "1"), показываем предупреждение
        if (firstQuestionAnswer === 'НЕТ') {
            setWarningModal({
                open: true,
                onContinue: () => {
                    setWarningModal({ open: false, onContinue: () => {} });
                    if (newAnswers.hadRevenueBeforeService === true) {
                        setStep(3);
                    } else {
                        handleSubmit(undefined, tariffFromAnswer);
                    }
                }
            });
        } else {
            if (newAnswers.hadRevenueBeforeService === true) {
                setStep(3);
            } else {
                // Вызов сразу после ответа: передаём тариф явно, т.к. setState ещё не применился
                handleSubmit(undefined, tariffFromAnswer);
            }
        }
    };

    const handleRevenueMonthChange = (month: number) => {
        const max = maxRevenueDate2026;
        const maxD = max
            ? getMaxDayForMonthWithCap(max, month)
            : daysInMonth(month, REVENUE_YEAR);
        const cappedMaxD = Math.max(1, maxD);
        const newDay = Math.min(revenueDay, cappedMaxD);
        setRevenueMonth(month);
        setRevenueDay(newDay);
        setAnswers({ ...answers, revenueStartDate2026: toIsoFromParts(month, newDay) });
    };

    const handleRevenueDayChange = (day: number) => {
        setRevenueDay(day);
        setAnswers({ ...answers, revenueStartDate2026: toIsoFromParts(revenueMonth, day) });
    };

    // Обработчики для формы (шаг 4)
    const handleUserDataChange = (field: string, value: string) => {
        setUserData({ ...userData, [field]: value });
    };

    const handleAgreementChange = (agreement: string) => {
        setAgreements({ ...agreements, [agreement]: !agreements[agreement as keyof typeof agreements] });
    };

    const validateUserData = (): Record<string, string> => {
        const err: Record<string, string> = {};
        if (!userData.fullName.trim()) err.fullName = "ФИО обязательно";
        if (!userData.inn.match(/^\d{10}$|^\d{12}$/)) err.inn = "ИНН должен содержать 10 или 12 цифр";
        if (!userData.phone.match(/^\+7\d{10}$/)) err.phone = "Телефон должен быть в формате +7XXXXXXXXXX";
        if (!userData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) err.email = "Введите корректный email";
        if (!userData.password.trim() || userData.password.length < 6) err.password = "Пароль должен содержать минимум 6 символов";
        if (!userData.registrationAddress.trim()) err.registrationAddress = "Адрес регистрации обязателен";
        if (!userData.passportSeries.match(/^\d{4}$/)) err.passportSeries = "Серия паспорта — 4 цифры";
        if (!userData.passportNumber.match(/^\d{6}$/)) err.passportNumber = "Номер паспорта — 6 цифр";
        if (!userData.passportIssueDate) err.passportIssueDate = "Дата выдачи паспорта обязательна";
        if (!userData.bankAccount.match(/^\d{20}$/)) err.bankAccount = "Расчетный счет — 20 цифр";
        if (!userData.bankBik.match(/^\d{9}$/)) err.bankBik = "БИК банка — 9 цифр";
        const snilsDigits = userData.snils.replace(/\D/g, '');
        if (!snilsDigits.match(/^\d{11}$/)) err.snils = "СНИЛС должен содержать 11 цифр";
        if (!agreements.offerAgreement) err.offerAgreement = "Необходимо принять условия оферты";
        if (!agreements.personalDataAgreement) err.personalDataAgreement = "Необходимо дать согласие на обработку персональных данных";
        return err;
    };

    const handleSubmit = async (salaryDateFromInput?: string, explicitTariff?: string) => {
        // Если мы на шаге 1 (форма), проверяем валидность
        if (step === 1) {
            const errors = validateUserData();
            setFieldErrors(errors);

            if (Object.keys(errors).length > 0) return;

            // Если форма валидна, дополнительно проверяем email на наличие аккаунта
            if (endpointAddress && userData.email) {
                try {
                    const url = `${endpointAddress}/checkEmail?email=${encodeURIComponent(userData.email)}`;
                    log('Register', 'Проверка email', { url });
                    const response = await fetch(url, { method: "GET" });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.status === "true" || data.status === true) {
                            log('Register', 'Email уже занят');
                            setFieldErrors((prev) => ({
                                ...prev,
                                email: "Аккаунт с таким email уже существует",
                            }));
                            return;
                        }
                    }
                } catch (e) {
                    log('Register', 'Ошибка проверки email, продолжаем', e);
                }
            }

            // Если форма валидна и email не занят, переходим к опроснику
            setStep(2);
            return;
        }

        if (step === 3) {
            const iso = answers.revenueStartDate2026;
            if (!isValidRevenueDate2026(iso)) {
                setModal({
                    open: true,
                    type: "error",
                    text: "Укажите корректную дату начала выручки в 2026 году.",
                });
                return;
            }
            if (!isRevenueDateNotInFuture(iso)) {
                setModal({
                    open: true,
                    type: "error",
                    text: "Нельзя указать дату в будущем. Выберите день и месяц не позже сегодняшней даты.",
                });
                return;
            }
        }

        // Если мы на шаге 2 или 3, завершаем регистрацию (явный тариф из анкеты приоритетнее state)
        try {
            const finalTariffType = explicitTariff ?? tariffType ?? "1";

            // Формируем тело запроса
            const requestBody = {
                password: userData.password,
                name: userData.fullName,
                inn: userData.inn,
                number: userData.phone,
                email: userData.email,
                "regist-address": userData.registrationAddress,
                "passport-series": userData.passportSeries,
                "passport-number": userData.passportNumber,
                "passport-gave": userData.passportIssuedBy,
                "passport-gave-date": userData.passportIssueDate,
                snils: userData.snils,
                "bank-name": userData.bankName,
                "bank-account": userData.bankAccount,
                "bank-bik": userData.bankBik,
                "tariff-type": finalTariffType,
                "salary-date": salaryDateFromInput ?? answers.revenueStartDate2026
            };

            log('Register', 'Отправка запроса регистрации', { endpoint: endpointAddress, tariffType: finalTariffType });

            if (endpointAddress) {
                const url = `${endpointAddress}/register`;
                fetch(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody)
                })
                .then(response => {
                    log('Register', 'Ответ сервера', { status: response.status });
                    if (!response.ok) {
                        throw new Error(`Ошибка при отправке данных на сервер: ${response.status} ${response.statusText}`);
                    }
                    return response.json();
                })
                .then(data => {
                    log('Register', 'Регистрация успешна, переход на подтверждение email', data);
                    navigate("/email-confirmation");
                })
                .catch(error => {
                    log('Register', 'Ошибка регистрации', error);
                    setModal({ open: true, type: "error", text: `Произошла ошибка при регистрации: ${error.message}. Пожалуйста, попробуйте позже.` });
                });
            } else {
                warn('Register', 'Endpoint не загружен, переход на подтверждение без запроса');
                navigate("/email-confirmation");
            }
        } catch (error) {
            setModal({ open: true, type: "error", text: "Произошла ошибка при регистрации. Пожалуйста, попробуйте позже." });
        }
    };

    const closeModal = () => {
        setModal((m) => {
            // Модальное окно успеха больше не используется, так как переход происходит напрямую
            return { ...m, open: false };
        });
    };

    // Шаг 3: Выбор даты
    const DateStep = () => (
        <div className="anketa-container">
            <div className="register-card">
                <div className="progress-section">
                    <div className="progress-header">
                        <span className="progress-text">
                            Шаг 3 из 3
                        </span>
                        <span className="progress-percentage">{Math.round(displayProgress)}%</span>
                    </div>
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{
                                width: `${displayProgress}%`,
                                transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        ></div>
                    </div>
                </div>

                <div className="question-section">
                    <h2 className="question-text">С какой даты в 2026 году пошла выручка?</h2>
                    <p className="question-hint">Выберите дату начала получения выручки</p>
                </div>

                <div className="date-input-section">
                    {!maxRevenueDate2026 ? (
                        <p className="revenue-date-unavailable">
                            Выбор даты начала выручки за {REVENUE_YEAR} год станет доступен с 1 января {REVENUE_YEAR}{" "}
                            года (нельзя указать дату, которая ещё не наступила).
                        </p>
                    ) : (
                        <>
                            <div className="revenue-date-picker" role="group" aria-label="Дата начала выручки в 2026 году">
                                <div className="revenue-date-row">
                                    <div className="revenue-date-field">
                                        <label className="revenue-date-label" htmlFor="revenue-day">День</label>
                                        <select
                                            id="revenue-day"
                                            className="revenue-date-select"
                                            value={revenueDay}
                                            onChange={(e) => handleRevenueDayChange(Number(e.target.value))}
                                        >
                                            {Array.from(
                                                {
                                                    length: Math.max(
                                                        1,
                                                        getMaxDayForMonthWithCap(maxRevenueDate2026, revenueMonth)
                                                    ),
                                                },
                                                (_, i) => i + 1
                                            ).map((d) => (
                                                <option key={d} value={d}>
                                                    {d}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="revenue-date-field">
                                        <label className="revenue-date-label" htmlFor="revenue-month">Месяц</label>
                                        <select
                                            id="revenue-month"
                                            className="revenue-date-select"
                                            value={revenueMonth}
                                            onChange={(e) => handleRevenueMonthChange(Number(e.target.value))}
                                        >
                                            {MONTH_NAMES_RU.slice(0, maxRevenueDate2026.getMonth() + 1).map((name, idx) => (
                                                <option key={idx + 1} value={idx + 1}>
                                                    {name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="revenue-date-field revenue-date-field-year">
                                        <span className="revenue-date-label">Год</span>
                                        <div className="revenue-year-fixed" aria-hidden="true">
                                            {REVENUE_YEAR}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="date-hint">
                                Только {REVENUE_YEAR} год. Нельзя выбрать дату позже сегодняшнего дня.
                            </div>
                        </>
                    )}
                </div>

                <div className="navigation-section">
                    <button className="nav-btn prev-btn" onClick={() => setStep(2)}>
                        ← Назад к вопросам
                    </button>
                    <button
                        className="nav-btn next-btn"
                        onClick={() => handleSubmit()}
                        disabled={!maxRevenueDate2026 || !isCompleteRevenueDate(answers.revenueStartDate2026)}
                    >
                        Завершить регистрацию
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Modal
                isOpen={modal.open}
                onClose={closeModal}
                title={modal.type === "success" ? "Успешно" : "Ошибка"}
                type={modal.type}
            >
                <p>{modal.text}</p>
            </Modal>
            <Modal
                isOpen={warningModal.open}
                onClose={() => warningModal.onContinue()}
                title="Предупреждение"
                type="info"
                buttonText="Продолжить"
            >
                <p>Если в течение года выручка превысит 20 млн. в 2026, 15 млн. в 2027, 10 млн. в 2028, клиент переходит на тариф УСН с НДС с месяца, следующего за месяцем превышения</p>
            </Modal>
            <Modal
                isOpen={documentModal.open}
                onClose={() => setDocumentModal({ open: false, type: "offer" })}
                title={documentModal.type === "offer" ? "Договор оферты" : "Согласие на обработку персональных данных"}
                type="info"
                buttonText="Закрыть"
            >
                <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "0.5rem 0" }}>
                    {documentModal.type === "offer" ? (
                        <div>
                            <h4 style={{ marginTop: 0, color: "var(--text-primary)" }}>ДОГОВОР ОФЕРТЫ</h4>
                            <p>Настоящий документ является публичной офертой (далее — «Оферта») на заключение договора оказания бухгалтерских услуг (далее — «Договор») на условиях, изложенных ниже.</p>
                            <h4 style={{ color: "var(--text-primary)" }}>1. ОБЩИЕ ПОЛОЖЕНИЯ</h4>
                            <p>1.1. Исполнитель обязуется оказать Заказчику услуги по ведению бухгалтерского учета и сдаче отчетности в соответствии с действующим законодательством Российской Федерации.</p>
                            <p>1.2. Заказчик обязуется предоставить Исполнителю всю необходимую документацию и информацию для выполнения обязательств по настоящему Договору.</p>
                            <h4 style={{ color: "var(--text-primary)" }}>2. ПРЕДМЕТ ДОГОВОРА</h4>
                            <p>2.1. Исполнитель оказывает следующие услуги:</p>
                            <ul>
                                <li>Ведение бухгалтерского учета</li>
                                <li>Подготовка и сдача налоговой отчетности</li>
                                <li>Консультации по вопросам налогообложения</li>
                                <li>Иные услуги, согласованные сторонами</li>
                            </ul>
                            <h4 style={{ color: "var(--text-primary)" }}>3. СТОИМОСТЬ УСЛУГ И ПОРЯДОК РАСЧЕТОВ</h4>
                            <p>3.1. Стоимость услуг определяется в соответствии с действующим тарифом Исполнителя.</p>
                            <p>3.2. Оплата услуг производится ежемесячно в порядке, установленном Договором.</p>
                            <h4 style={{ color: "var(--text-primary)" }}>4. ОТВЕТСТВЕННОСТЬ СТОРОН</h4>
                            <p>4.1. Исполнитель несет ответственность за качество оказываемых услуг в соответствии с действующим законодательством.</p>
                            <p>4.2. Заказчик несет ответственность за достоверность предоставленной информации и документации.</p>
                            <h4 style={{ color: "var(--text-primary)" }}>5. ЗАКЛЮЧИТЕЛЬНЫЕ ПОЛОЖЕНИЯ</h4>
                            <p>5.1. Акцептом настоящей Оферты является совершение Заказчиком действий по регистрации на сайте Исполнителя и оплате услуг.</p>
                            <p>5.2. Настоящая Оферта вступает в силу с момента акцепта Заказчиком и действует до момента расторжения Договора.</p>
                        </div>
                    ) : (
                        <div>
                            <h4 style={{ marginTop: 0, color: "var(--text-primary)" }}>СОГЛАСИЕ НА ОБРАБОТКУ ПЕРСОНАЛЬНЫХ ДАННЫХ</h4>
                            <p>Настоящим я даю свое согласие на обработку моих персональных данных в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных».</p>
                            <h4 style={{ color: "var(--text-primary)" }}>1. ОПРЕДЕЛЕНИЯ</h4>
                            <p>1.1. Персональные данные — любая информация, относящаяся к прямо или косвенно определенному или определяемому физическому лицу (субъекту персональных данных).</p>
                            <p>1.2. Оператор — организация, осуществляющая обработку персональных данных.</p>
                            <h4 style={{ color: "var(--text-primary)" }}>2. СОСТАВ ПЕРСОНАЛЬНЫХ ДАННЫХ</h4>
                            <p>2.1. Я даю согласие на обработку следующих персональных данных:</p>
                            <ul>
                                <li>Фамилия, имя, отчество</li>
                                <li>ИНН</li>
                                <li>Контактные данные (телефон, электронная почта)</li>
                                <li>Адрес регистрации</li>
                                <li>Паспортные данные</li>
                                <li>Банковские реквизиты</li>
                                <li>Иная информация, необходимая для оказания услуг</li>
                            </ul>
                            <h4 style={{ color: "var(--text-primary)" }}>3. ЦЕЛИ ОБРАБОТКИ</h4>
                            <p>3.1. Персональные данные обрабатываются в следующих целях:</p>
                            <ul>
                                <li>Оказание бухгалтерских услуг</li>
                                <li>Ведение бухгалтерского учета</li>
                                <li>Подготовка и сдача отчетности</li>
                                <li>Связь с клиентом по вопросам оказания услуг</li>
                                <li>Исполнение обязательств по договору</li>
                            </ul>
                            <h4 style={{ color: "var(--text-primary)" }}>4. СПОСОБЫ ОБРАБОТКИ</h4>
                            <p>4.1. Обработка персональных данных осуществляется с использованием средств автоматизации и без использования таких средств.</p>
                            <p>4.2. Оператор вправе передавать персональные данные третьим лицам только в случаях, предусмотренных законодательством Российской Федерации.</p>
                            <h4 style={{ color: "var(--text-primary)" }}>5. СРОК ДЕЙСТВИЯ СОГЛАСИЯ</h4>
                            <p>5.1. Настоящее согласие действует с момента его предоставления до момента отзыва согласия.</p>
                            <p>5.2. Я имею право отозвать настоящее согласие в любое время путем направления письменного уведомления Оператору.</p>
                            <h4 style={{ color: "var(--text-primary)" }}>6. ПРАВА СУБЪЕКТА ПЕРСОНАЛЬНЫХ ДАННЫХ</h4>
                            <p>6.1. Я имею право:</p>
                            <ul>
                                <li>Получать информацию, касающуюся обработки моих персональных данных</li>
                                <li>Требовать уточнения, блокирования или уничтожения персональных данных</li>
                                <li>Отозвать согласие на обработку персональных данных</li>
                                <li>Обжаловать действия или бездействие Оператора в уполномоченный орган</li>
                            </ul>
                        </div>
                    )}
                </div>
            </Modal>
            {step === 1 ? (
                // Шаг 1: Форма пользователя
                <div className="anketa-container">
                    <div className="register-card">
                        <div className="progress-section">
                            <div className="progress-header">
                        <span className="progress-text">
                            Шаг 1 из 3
                        </span>
                                <span className="progress-percentage">{Math.round(displayProgress)}%</span>
                            </div>
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{
                                        width: `${displayProgress}%`,
                                        transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                ></div>
                            </div>
                        </div>

                        <div className="question-section">
                            <h2 className="question-text">Заполните данные для регистрации</h2>
                            <p className="question-hint">Все поля обязательны для заполнения</p>
                        </div>

                        <div className="form-section">
                            <div className="form-group">
                                <label>ФИО*</label>
                                <input
                                    type="text"
                                    value={userData.fullName}
                                    onChange={(e) => handleUserDataChange("fullName", e.target.value)}
                                    placeholder="Фамилия Имя Отчество"
                                />
                                {fieldErrors.fullName && <div className="field-error">{fieldErrors.fullName}</div>}
                            </div>

                            <div className="form-group">
                                <label>ИНН*</label>
                                <input
                                    type="text"
                                    value={userData.inn}
                                    onChange={(e) => handleUserDataChange("inn", e.target.value.replace(/\D/g, ''))}
                                    placeholder="123456789012"
                                    maxLength={12}
                                />
                                {fieldErrors.inn && <div className="field-error">{fieldErrors.inn}</div>}
                            </div>

                            <div className="form-group">
                                <label>Телефон*</label>
                                <input
                                    type="tel"
                                    value={userData.phone}
                                    onChange={(e) => handleUserDataChange("phone", e.target.value)}
                                    placeholder="+79991234567"
                                />
                                {fieldErrors.phone && <div className="field-error">{fieldErrors.phone}</div>}
                            </div>

                            <div className="form-group">
                                <label>Email*</label>
                                <input
                                    type="email"
                                    value={userData.email}
                                    onChange={(e) => handleUserDataChange("email", e.target.value)}
                                    placeholder="example@example.com"
                                />
                                {fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
                            </div>

                            <div className="form-group">
                                <label>Пароль*</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={userData.password}
                                        onChange={(e) => handleUserDataChange("password", e.target.value)}
                                        placeholder="Введите пароль"
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle-btn"
                                        onClick={() => setShowPassword(!showPassword)}
                                        title={showPassword ? "Скрыть пароль" : "Показать пароль"}
                                    >
                                        {showPassword ? "👁️" : "👁️‍🗨️"}
                                    </button>
                                </div>
                                {fieldErrors.password && <div className="field-error">{fieldErrors.password}</div>}
                            </div>

                            <div className="form-group">
                                <label>Адрес регистрации*</label>
                                <input
                                    type="text"
                                    value={userData.registrationAddress}
                                    onChange={(e) => handleUserDataChange("registrationAddress", e.target.value)}
                                    placeholder="г. Москва, ул. Ленина, д. 1, кв. 1"
                                />
                                {fieldErrors.registrationAddress && <div className="field-error">{fieldErrors.registrationAddress}</div>}
                            </div>

                            <div className="form-group">
                                <label>Серия паспорта*</label>
                                <input
                                    type="text"
                                    value={userData.passportSeries}
                                    onChange={(e) => handleUserDataChange("passportSeries", e.target.value.replace(/\D/g, ''))}
                                    placeholder="1234"
                                    maxLength={4}
                                />
                                {fieldErrors.passportSeries && <div className="field-error">{fieldErrors.passportSeries}</div>}
                            </div>

                            <div className="form-group">
                                <label>Номер паспорта*</label>
                                <input
                                    type="text"
                                    value={userData.passportNumber}
                                    onChange={(e) => handleUserDataChange("passportNumber", e.target.value.replace(/\D/g, ''))}
                                    placeholder="123456"
                                    maxLength={6}
                                />
                                {fieldErrors.passportNumber && <div className="field-error">{fieldErrors.passportNumber}</div>}
                            </div>

                            <div className="form-group">
                                <label>СНИЛС*</label>
                                <input
                                    type="text"
                                    value={userData.snils}
                                    onChange={(e) => handleUserDataChange("snils", e.target.value)}
                                    placeholder="123-456-789 01"
                                    maxLength={14}
                                />
                                {fieldErrors.snils && <div className="field-error">{fieldErrors.snils}</div>}
                            </div>

                            <div className="form-group">
                                <label>Кем выдан</label>
                                <input
                                    type="text"
                                    value={userData.passportIssuedBy}
                                    onChange={(e) => handleUserDataChange("passportIssuedBy", e.target.value)}
                                    placeholder="Отделением УФМС России"
                                />
                                {fieldErrors.passportIssuedBy && <div className="field-error">{fieldErrors.passportIssuedBy}</div>}
                            </div>

                            <div className="form-group">
                                <label>Дата выдачи*</label>
                                <input
                                    type="date"
                                    value={userData.passportIssueDate}
                                    onChange={(e) => handleUserDataChange("passportIssueDate", e.target.value)}
                                />
                                {fieldErrors.passportIssueDate && <div className="field-error">{fieldErrors.passportIssueDate}</div>}
                            </div>

                            <div className="form-group">
                                <label>Название банка</label>
                                <input
                                    type="text"
                                    value={userData.bankName}
                                    onChange={(e) => handleUserDataChange("bankName", e.target.value)}
                                    placeholder="ПАО Сбербанк"
                                />
                                {fieldErrors.bankName && <div className="field-error">{fieldErrors.bankName}</div>}
                            </div>

                            <div className="form-group">
                                <label>Расчетный счет*</label>
                                <input
                                    type="text"
                                    value={userData.bankAccount}
                                    onChange={(e) => handleUserDataChange("bankAccount", e.target.value.replace(/\D/g, ''))}
                                    placeholder="40702810123456789012"
                                    maxLength={20}
                                />
                                {fieldErrors.bankAccount && <div className="field-error">{fieldErrors.bankAccount}</div>}
                            </div>

                            <div className="form-group">
                                <label>БИК банка*</label>
                                <input
                                    type="text"
                                    value={userData.bankBik}
                                    onChange={(e) => handleUserDataChange("bankBik", e.target.value.replace(/\D/g, ''))}
                                    placeholder="044525225"
                                    maxLength={9}
                                />
                                {fieldErrors.bankBik && <div className="field-error">{fieldErrors.bankBik}</div>}
                            </div>
                        </div>

                        <div className="agreements-section">
                            <div className="agreement-item">
                                <label className="agreement-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={agreements.offerAgreement}
                                        onChange={() => handleAgreementChange("offerAgreement")}
                                    />
                                    <span className="checkmark"></span>
                                    <span className="agreement-text">
                                        Я принимаю условия <button type="button" className="agreement-link" onClick={(e) => { e.preventDefault(); setDocumentModal({ open: true, type: "offer" }); }}>Договора оферты</button>
                                    </span>
                                </label>
                                {fieldErrors.offerAgreement && <div className="field-error">{fieldErrors.offerAgreement}</div>}
                            </div>

                            <div className="agreement-item">
                                <label className="agreement-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={agreements.personalDataAgreement}
                                        onChange={() => handleAgreementChange("personalDataAgreement")}
                                    />
                                    <span className="checkmark"></span>
                                    <span className="agreement-text">
                                        Я даю согласие на обработку <button type="button" className="agreement-link" onClick={(e) => { e.preventDefault(); setDocumentModal({ open: true, type: "privacy" }); }}>персональных данных</button>
                                    </span>
                                </label>
                                {fieldErrors.personalDataAgreement && <div className="field-error">{fieldErrors.personalDataAgreement}</div>}
                            </div>
                        </div>

                        <div className="navigation-section">
                            <button className="nav-btn prev-btn" onClick={() => {
                                navigate("/");
                            }}>
                                ← На главную
                            </button>
                            <button className="nav-btn submit-btn" onClick={() => handleSubmit()}>
                                Далее →
                            </button>
                        </div>
                    </div>

                    <div className="register-footer">
                        <p>
                            Уже есть аккаунт? <a href="/login" className="login-link">Войти</a>
                        </p>
                    </div>
                </div>
            ) : step === 2 ? (
                // Шаг 2: Второй опросник
                <Questionnaire
                    questions={initialQuestions}
                    title="Регистрация: Вопросы"
                    onComplete={handleQuestionnaireComplete}
                    showCorrectAnswers={false}
                    successMessage=""
                />
            ) : (
                // Шаг 3: Выбор даты
                <DateStep />
            )}
        </>
    );
};